/* eslint-disable max-classes-per-file */
import { getCategories } from './api/marketplace';
import fetchGoogleTaxonomy from './fetchGoogleTaxonomy';
import type { IconName } from 'storybookConfig/mixins/icons';
// keep fetched taxonomy data in memory so we don't have to fetch again
let googleCategoryData: any = null;
let staticCategoryData: any = null;

/**
 * each node in the Trie represents a single google product category at a particular level
 * a node can have subcategories (children), and also a code
 * if a node has a code, it represents a final subcategory in the google taxonomy
 */
export class CategoryTrieNode {
  category: string;

  level: number;

  code: string;

  children: { [key: string]: CategoryTrieNode };

  constructor(category: string, level: number, code = '') {
    this.category = category; // the text representing the name of the category at this level
    this.level = level; // how far down the tree is this node
    this.code = code; // the actual category ID, should only be available if this node represents a terminal node
    this.children = {}; // hash to represent children
  }

  addChild(category: string, level: number, code = '') {
    if (!this.children[category]) {
      this.children[category] = new CategoryTrieNode(category, level, code);
    }
    const childNode = this.children[category];
    // node can have children and also be a leaf (final subcategory)
    // if the children are added before
    childNode.code = childNode.code || code;
    return childNode;
  }

  hasChild(category: string) {
    return !!this.children[category];
  }

  getChild(category: string) {
    return this.children[category];
  }

  hasChildren() {
    return Object.keys(this.children).length === 0;
  }

  /**
   * gets a list of the child categories, useful to display next level of options
   * @returns array of categories that are nested one level under this node
   */
  getChildCategories() {
    return Object.keys(this.children);
  }
}

/**
 * data structure used to give hierarchy and easy access to all the google product categories
 * should only need to be built once when category JSON loads
 */
export class CategoryTrie {
  head: CategoryTrieNode;

  constructor() {
    this.head = new CategoryTrieNode('ROOT', 0);
  }

  /**
   * called for each category returned in the google taxonomy JSON in order to build up the trie
   * @param categoryPath the category hierarchy in the form of "a > b > c"
   * @param code the Google Product Category code that matches this category hierarchy
   */
  addGoogleCategory(categoryPath: string, code: string) {
    const categories = categoryPath.split('>').map((c) => c.trim());
    let currentNode = this.head;

    categories.forEach((category, idx) => {
      const isLast = idx === categories.length - 1;
      currentNode = currentNode.addChild(category, idx + 1, isLast ? code : '');
    });
  }

  /**
   * takes a category hierarchy and returns the appropriate code
   * @param categoryPath can be string "a > b > c" or array ["a", "b", "c"]
   * @returns the code or "" if the category path doesn't match
   */
  getCodeByPath(categoryPath: string[] | string = []) {
    const categories =
      typeof categoryPath === 'string'
        ? categoryPath.split('>').map((c) => c.trim())
        : categoryPath;
    let currentNode = this.head;
    for (let i = 0; i < categories.length; i += 1) {
      // if path isn't valid, return empty
      if (!currentNode.hasChild(categories[i])) {
        return '';
      }
      currentNode = currentNode.getChild(categories[i]);
    }
    // found last node
    return currentNode.code;
  }

  /**
   * takes a partial category hierarchy and returns the subcategories
   * @param categoryPath can be string "a > b > c" or array ["a", "b", "c"]
   * @returns a list of subcategories, null if there are no more subcategories, or path is invalid
   */
  getSubcategoriesByPath(categoryPath: string[] | string = []) {
    const categories =
      typeof categoryPath === 'string'
        ? categoryPath
            .split('>')
            .map((c) => c.trim())
            .filter((c) => c)
        : categoryPath;
    let currentNode = this.head;
    for (let i = 0; i < categories.length; i += 1) {
      // if path isn't valid, return empty
      if (!currentNode.hasChild(categories[i])) {
        return null;
      }
      currentNode = currentNode.getChild(categories[i]);
    }
    // found last node
    const subcategories = currentNode.getChildCategories();
    return subcategories.length === 0 ? null : subcategories;
  }
}

export interface GoogleCategoryData {
  trie: CategoryTrie;
  list: Array<{ code: string; name: string }>;
  map?: Map<string, string>;
}

const buildTrieAndList = (data: any): GoogleCategoryData | null => {
  if (!data) return null;
  const trie = new CategoryTrie();
  const list: Array<{ code: string; name: string }> = [];
  const map = new Map();
  map.set('0', 'All Categories');
  Object.keys(data)
    .filter((key) => key !== '0')
    .forEach((key) => {
      trie.addGoogleCategory(data[key], key);
      list.push({ code: key, name: data[key] });
      map.set(key, data[key]);
    });

  return { trie, list, map };
};

/**
 * Fetches the Google Product Taxonomy JSON and returns parsed data structures used when assigning a category
 * @returns object containing the trie and list data structures used in the picker
 */
export const fetchGoogleCategories = async (): Promise<GoogleCategoryData | null> => {
  try {
    // if data loaded into memory, return it, don't fetch
    if (Object.keys(googleCategoryData?.data?.categories || {}).length > 0) {
      return buildTrieAndList(googleCategoryData?.data?.categories);
    }
    // if not in memory, pull from API and parse
    googleCategoryData = await getCategories();

    if (Object.keys(googleCategoryData?.data?.categories || {}).length > 0) {
      return buildTrieAndList(googleCategoryData.data.categories);
    }
  } catch (err) {
    console.error(err);
  }
  return null;
};

export const fetchStaticCategoryList = async (): Promise<GoogleCategoryData | null> => {
  try {
    // if data loaded into memory, return it, don't fetch
    if (staticCategoryData) {
      return buildTrieAndList(staticCategoryData);
    }
    // if not in memory, pull from json file
    staticCategoryData = await fetchGoogleTaxonomy();

    return buildTrieAndList(staticCategoryData);
  } catch (err) {
    console.error(err);
  }
  return null;
};

export const rootCategoryMap: Record<number, { id: string; icon: IconName; name: string }> = {
  0: { id: '0', icon: 'layout-grid', name: 'All Categories' },
  1: { id: '1', icon: 'paw-print', name: 'Animals & Pets' },
  8: { id: '8', icon: 'drama', name: 'Arts & Entertainment' },
  166: { id: '166', icon: 'shirt', name: 'Apparel' },
  111: { id: '111', icon: 'briefcase-business', name: 'Business' },
  141: { id: '141', icon: 'camera', name: 'Cameras & Optics' },
  222: { id: '222', icon: 'monitor-smartphone', name: 'Electronics' },
  412: { id: '412', icon: 'coffee', name: 'Food & Beverage' },
  436: { id: '436', icon: 'armchair', name: 'Furniture' },
  469: { id: '469', icon: 'flower-2', name: 'Health & Beauty' },
  536: { id: '536', icon: 'sprout', name: 'Home & Garden' },
  537: { id: '537', icon: 'baby', name: 'Baby & Toddler' },
  632: { id: '632', icon: 'hammer', name: 'Hardware' },
  772: { id: '772', icon: 'triangle-alert', name: 'Mature' },
  783: { id: '783', icon: 'book-open', name: 'Media' },
  888: { id: '888', icon: 'car', name: 'Vehicles & Parts' },
  922: { id: '922', icon: 'pencil', name: 'Office Supplies' },
  988: { id: '988', icon: 'volleyball', name: 'Sporting Goods' },
  1239: { id: '1239', icon: 'toy-brick', name: 'Toys & Games' },
  2092: { id: '2092', icon: 'monitor', name: 'Software' },
  5181: { id: '5181', icon: 'luggage', name: 'Luggage & Bags' },
  5605: { id: '5605', icon: 'church', name: 'Religious' },
};
